import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, ShoppingCart } from 'lucide-react';
import './PurchasePanel.css';

/**
 * Purchase UI for a calculated band.
 *
 * No account is required to buy. Stripe Checkout collects the email, and the
 * entitlement is attached to the band's UUID — so the flow is: calculate, pay,
 * download. Accounts only become necessary when someone wants to come back
 * later and re-download, which is a separate feature.
 *
 * The visible lock here is a convenience, not the enforcement. The server checks
 * entitlement on the download route; anything decided in the browser is only
 * ever a hint.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLURB = {
  digital: 'Print-resolution image, the star ledger PDF, and the full dataset.',
  print: 'Giclée on archival cotton rag, cut to the ribbon. Ships from the USA.',
  framed: 'Framed behind a passe-partout mount, ready to hang.',
};

const money = (cents, currency) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(cents / 100);

export default function PurchasePanel({ bandId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!bandId) return;
    try {
      const { data } = await axios.get(`${API}/zenith/band/${bandId}/entitlement`);
      setState(data);
    } catch {
      setState(null); // Panel simply doesn't render — never blocks the result.
    }
  }, [bandId]);

  useEffect(() => { load(); }, [load]);

  // Returning from Stripe. Fulfilment happens in the webhook, so the paid flag
  // can lag the redirect by a moment — poll briefly rather than showing a
  // customer who just paid that they haven't.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('purchase') !== 'success') return;

    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/zenith/band/${bandId}/entitlement`);
        setState(data);
        if (data.paid) {
          clearInterval(id);
          toast.success('Payment received — your downloads are unlocked.');
          window.history.replaceState({}, '', window.location.pathname);
        }
      } catch { /* keep trying */ }
      if (tries >= 10) clearInterval(id);
    }, 1500);

    return () => clearInterval(id);
  }, [bandId]);

  const buy = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, { band_id: bandId, tier });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setBusy(null);
    }
  };

  if (!state || !state.payments_enabled) return null;

  if (state.paid) {
    return (
      <div className="pp pp-paid">
        <Check className="w-5 h-5 shrink-0" />
        <div>
          <h3 className="pp-paid-title">Unlocked</h3>
          <p className="pp-paid-body">
            The print-resolution image and star ledger for this band are yours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pp">
      <div className="pp-head">
        <h3 className="pp-title">Keep this band</h3>
        <p className="pp-sub">
          The view above is free for as long as you like. These are the versions you keep.
        </p>
      </div>

      <div className="pp-tiers">
        {Object.entries(state.tiers).map(([key, tier]) => (
          <div className={`pp-tier${key === 'digital' ? ' pp-tier-featured' : ''}`} key={key}>
            <div className="pp-tier-top">
              <span className="pp-tier-name">{tier.name}</span>
              <span className="pp-tier-price">{money(tier.amount_cents, tier.currency)}</span>
            </div>
            <p className="pp-tier-blurb">{BLURB[key]}</p>
            <button
              className="pp-buy"
              onClick={() => buy(key)}
              disabled={busy !== null}
              data-testid={`buy-${key}`}
            >
              {busy === key
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                : <><ShoppingCart className="w-4 h-4" /> Buy</>}
            </button>
          </div>
        ))}
      </div>

      <p className="pp-foot">
        Secure checkout by Stripe. No account needed — your receipt goes to the email
        you enter.
      </p>
    </div>
  );
}
